import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useIsMobile } from '@/hooks/useIsMobile'
import { trackNewsletterShown } from '@/lib/track'
import { useNewsletterPopup } from './useNewsletterPopup'
import { NewsletterPopupDesktop } from './NewsletterPopupDesktop'
import { NewsletterPopupMobile } from './NewsletterPopupMobile'

export function NewsletterPopup() {
  const { open, close, markDismissed } = useNewsletterPopup()
  const isMobile = useIsMobile()
  const trackedRef = useRef(false)

  useEffect(() => {
    if (!open) return

    if (!trackedRef.current) {
      trackedRef.current = true
      trackNewsletterShown()
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, close])

  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      {/* Clicking the dimmed backdrop closes the popup. Mouse-only shortcut:
          keyboard users have Escape and the popup's own close button. */}
      <button
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        onClick={close}
        className="absolute inset-0 cursor-default"
      />
      <div className="relative">
        {isMobile ? (
          <NewsletterPopupMobile onClose={close} onSubscribed={markDismissed} />
        ) : (
          <NewsletterPopupDesktop onClose={close} onSubscribed={markDismissed} />
        )}
      </div>
    </div>,
    document.body,
  )
}

export interface NewsletterPopupVariantProps {
  onClose: () => void
  onSubscribed: () => void
}
